import Input from '@/components/Input';
import ModalV2 from '@/components/ModalV2';
import Spinner from '@/components/Spinner';
import useChangeCompany from '@/hooks/useChangeCompany';
import getCompanyIdForRequest from '@/lib/getCompanyIdForRequest.js';
import getDateTimeAgo from '@/lib/getDateTimeAgo';
import { getMyStaffAccess, postStaffAccessRequest } from '@/redux/actions/staffAccessActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { getUserCompanies } from '@/redux/actions/userActions';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import { modal } from '@/redux/selectors/uiSelectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isCompanyStaffAccessRestricted, ROUTE_APP, STAFF_ACCESS_REASON_MAX_LENGTH } from 'shared/schema/staffAccess.js';

/* Opens when a MindCloud staff member is refused a restricted organization (lib/handleOrgAccessRestricted.js).
   Mounted beside UserStateHandler so it also shows when the tab has no organization it can load. */
const RequestOrgAccessModal = () => {
  const modalState = useSelector(memoizeSelector('modal:requestOrgAccess', (state) => modal(state, 'requestOrgAccess')));
  const show = modalState?.open === true;
  const companyId = modalState?.companyId;
  const leaveCompany = modalState?.leaveCompany === true;
  const userId = useSelector((state) => state?.user?.jwt?.id);
  const changeCompany = useChangeCompany();
  const [access, setAccess] = useState(null);
  const [status, setStatus] = useState('loading');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !companyId) {
      return;
    }

    let active = true;
    setAccess(null);
    setStatus('loading');
    setReason('');
    setError('');
    getMyStaffAccess({ companyId }, (data, requestError) => {
      if (!active) {
        return;
      }
      if (requestError || !data?.company) {
        setError(requestError?.message || 'This organization could not be loaded.');
        setStatus('error');
        return;
      }
      setAccess(data);
      setStatus('ready');
    });

    return () => {
      active = false;
    };
  }, [show, companyId]);

  /* The refused organization is the one this tab loads, so every request would be refused again. Like Cirra's startup,
     move the tab to the person's own personal account (never restricted); without one, to the first organization that
     is not restricted. It still moves if the modal closes first, since the tab cannot stay here. */
  useEffect(() => {
    if (!show || !leaveCompany || !companyId || !userId) {
      return;
    }

    getUserCompanies({
      userId,
      callback: (data) => {
        const nextCompanyId = data?.ownPersonalCompanyId || data?.companies?.find((company) => company.id !== companyId && !isCompanyStaffAccessRestricted(company))?.id;
        if (nextCompanyId && getCompanyIdForRequest() === companyId) {
          changeCompany(nextCompanyId, { message: 'Please wait while we change companies...', redirect: true });
        }
      }
    });
  }, [show, leaveCompany, companyId, userId]);

  const handleClose = () => setModalState('requestOrgAccess', { open: false, companyId: null });

  const handleOpen = () => {
    handleClose();
    changeCompany(companyId, { message: 'Please wait while we change companies...', redirect: true });
  };

  const handleSubmit = () => {
    setStatus('sending');
    setError('');
    postStaffAccessRequest({ companyId, reason: reason.trim() }, (_data, requestError) => {
      if (requestError) {
        setError(requestError.message || 'Your request could not be sent. Please retry.');
        setStatus('ready');
        return;
      }
      setStatus('sent');
    });
  };

  const companyName = access?.company?.name;
  const gravityAccess = access?.apps?.[ROUTE_APP.gravity];
  const canOpen = status === 'ready' && (access?.company?.restricted === false || gravityAccess?.approved === true);
  const pending = status === 'ready' && !canOpen ? gravityAccess?.pending : null;
  const showForm = (status === 'ready' || status === 'sending') && !canOpen && !pending;
  const lastDenied = showForm && gravityAccess?.lastDeniedOn ? getDateTimeAgo(gravityAccess.lastDeniedOn) : null;
  const trimmedReason = reason.trim();

  // Outside the form the status is the body text: a message banner under an empty body leaves a gap below the title.
  const view = (() => {
    if (status === 'sent') {
      return { text: 'Request sent. A MindCloud administrator will review it, and you will get an email when it is decided.', actions: [{ children: 'Close', className: 'primary', onClick: handleClose }] };
    }
    if (canOpen) {
      return {
        text: `You can access ${companyName} in Gravity.`,
        actions: [
          { children: 'Cancel', onClick: handleClose },
          { children: 'Access organization', className: 'primary', onClick: handleOpen }
        ]
      };
    }
    if (pending) {
      return { text: `Your request from ${getDateTimeAgo(pending.requestedOn)} is waiting for a MindCloud administrator. You will get an email when it is decided.`, actions: [{ children: 'Close', className: 'primary', onClick: handleClose }] };
    }
    if (status === 'error') {
      return { text: error, actions: [{ children: 'Close', onClick: handleClose }] };
    }
    return {
      message: error || (lastDenied ? `Your last request was declined ${lastDenied}. You can ask again.` : ''),
      messageType: error ? 'error' : 'warning',
      actions: [
        { children: 'Cancel', onClick: handleClose, disabled: status === 'sending' },
        { children: 'Submit', className: 'primary', onClick: handleSubmit, disabled: !trimmedReason || status !== 'ready', loading: status === 'sending' }
      ]
    };
  })();

  return (
    <ModalV2 //
      show={show}
      portal
      maxWidth={'480px'}
      className={'request-org-access-modal'}
      onHide={handleClose}
      title={companyName ? `Request access to ${companyName}` : 'Request access'}
      description={showForm ? 'Please specify why you need access to this organization, so it can be approved.' : undefined}
      message={view.message}
      messageType={view.messageType}
      actions={view.actions}
      autoFocusRef={'textarea'}>
      {status === 'loading' && <Spinner />}
      {view.text && <p className={'staff-access-status'}>{view.text}</p>}
      {showForm && <Input type={'textarea'} rows={5} label={'Reason'} placeholder={'What you will work on, and for whom'} value={reason} maxLength={STAFF_ACCESS_REASON_MAX_LENGTH} onChange={(event) => setReason(event.target.value)} disabled={status === 'sending'} />}
      {pending?.reason && <p className={'staff-access-reason'}>{pending.reason}</p>}
    </ModalV2>
  );
};

export default RequestOrgAccessModal;
